<template>
  <BaseSelectDropdown
    v-model="isOpen"
    :show-dropdown-icon="false"
    :allow-width-overflow="true"
    :dropdown-width="700"
    :close-on-click-outside="false"
    :small="small"
    visible-arrow
    :select-dropdown-class="[
      'select-control__no-padding',
      'select-control__no-height-limit'
    ]"
  >
    <template slot="header">
      <div
        v-if="value.length > 0"
        class="multi-select-value"
      >
        <div
          v-if="valueIcon"
          class="multi-select-value__icon"
        >
          <MIcon
            :icon="valueIcon"
          />
        </div>
        <div class="multi-select-value__text">
          {{ formattedValueText }}
        </div>
      </div>
      <div
        v-else
        v-overflow-title="placeholder"
        class="multi-select-placeholder"
      >
        {{ placeholder }}
      </div>
    </template>
    <template slot="dropdown">
      <MultiSelectDropdown
        :value="innerValue"
        :active-group-id="activeGroupId"
        :choices="choices"
        :choices-flat="choicesFlat"
        :disable-group-select="disableGroupSelect"
        :group-name-text="groupNameText"
        :entity-name-text="entityNameText"
        :title="modalTitle"
        :select-all-text="selectAllText"
        :select-all-hint="selectAllHint"
        :format-selected-counter-text="formatSelectedCounterText"
        :select-all-predicate="selectAllPredicate"
        :allow-save-predicate="allowSavePredicate"
        :error-text="errorText"
        :ok-text="okText"
        :cancel-text="cancelText"
        @apply="handleApply"
        @cancel="handleCancel"
        @input="handleItemsInput"
        @selectGroup="handleSelectActiveGroup"
      />
    </template>
  </BaseSelectDropdown>
</template>

<script>
  import BaseSelectDropdown from 'components/Inputs/Select/common/BaseSelectDropdown/BaseSelectDropdown';
  import MultiSelectDropdown from 'components/Inputs/Select/MultiSelectInput/MultiSelectDropdown';
  import MIcon from 'components/Design/MIcon';
  import getTr from 'translation';
  export default {
    name: 'MultiSelectInput',
    components: {MultiSelectDropdown, BaseSelectDropdown, MIcon},
    props: {
      value: {
        type: Array,
        required: true
      },
      choices: {
        type: Array,
        required: true
      },
      placeholder: {
        type: String,
        default: ''
      },
      modalTitle: {
        type: String,
        required: true
      },
      entityNameText: {
        type: String,
        default: ''
      },
      groupNameText: {
        type: String,
        default: ''
      },
      forceGroupId: {
        type: Number,
        default: undefined
      },
      disableGroupSelect: {
        type: Boolean,
        default: false
      },
      formatValueText: {
        type: Function,
        default: null
      },
      formatSelectedCounterText: {
        type: Function,
        default: null
      },
      selectAllText: {
        type: String,
        default: undefined
      },
      selectAllPredicate: {
        type: Function,
        default: () => true
      },
      selectAllHint: {
        type: String,
        default: null
      },
      allowSavePredicate: {
        type: Function,
        default: () => true
      },
      errorText: {
        type: String,
        default: ''
      },
      okText: {
        type: String,
        default: 'OK'
      },
      cancelText: {
        type: String,
        default: () => getTr('cancel')
      },
      small: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isOpen: false,
        activeGroupId: this.forceGroupId,
        innerValue: [...this.value]
      };
    },
    computed: {
      choicesFlat () {
        return this.choices.map(c => c.children).flat();
      },
      activeGroup () {
        if (!this.activeGroupId) {
          return null;
        }
        return this.choices.find(c => c.id === this.activeGroupId);
      },
      valueIcon () {
        if (this.activeGroup) {
          return this.activeGroup.icon;
        }
        return null;
      },
      formattedValueText () {
        if (this.formatValueText) {
          return this.formatValueText(this.value, this.choicesFlat);
        }
        return `${this.value.length} Items`;
      }
    },
    watch: {
      value () {
        this.innerValue = [...this.value];
        this.fillGroupByInnerValue();
      }
    },
    mounted () {
      this.fillGroupByInnerValue();
    },
    methods: {
      fillGroupByInnerValue () {
        // do not fill group if no value selected or active group is not FORCED by prop
        if (this.innerValue.length > 0 && !this.activeGroupId) {
          this.setChoicesGroup();
        }
      },
      setChoicesGroup () {
        // we assume that all choices are from same group
        const chosenItemId = this.value[0];

        for (let c of this.choices) {
          const children = c.children;
          const existing = children.find(c => c.id === chosenItemId);
          if (existing) {
            this.activeGroupId = c.id;
            break;
          }
        }
      },
      handleSelectActiveGroup (groupId) {
        this.activeGroupId = groupId;
        this.innerValue = [];
      },
      handleItemsInput (items) {
        this.innerValue = items;
      },
      handleApply () {
        this.$emit('input', this.innerValue);
        this.isOpen = false;
      },
      handleCancel () {
        this.innerValue = [...this.value];
        this.isOpen = false;
      }
    }
  };
</script>

<style>
  .multi-select-placeholder {
    color: var(--gray-text-color);
    text-overflow: ellipsis;

    overflow: hidden;
  }

  .multi-select-value {
    display: flex;
    align-items: center;
  }

  .multi-select-value__icon {
    flex-grow: 0;
    flex-shrink: 0;

    width: 14px;
    height: 14px;

    margin-right: 8px;
  }

  .multi-select-value__text {
    color: var(--secondary-text-color);
    text-overflow: ellipsis;

    overflow: hidden;
  }

</style>
